"use client";
import { createContext, useContext, useState } from "react";
import type {
  WhiteLabel,
  EventNetworkingSettings,
  Integration,
} from "database";
import type { ResolvedBranding } from "@/lib/branding/types";

type WhiteLabelContextType = {
  whiteLabel: (WhiteLabel & { integrations?: Integration[] }) | null;
  /** @deprecated Prefer useBrandingStore for branding fields */
  branding: ResolvedBranding | null;
  currentEventNetworkingSettings: EventNetworkingSettings | null;
  setCurrentEventNetworkingSettings: (
    networkingSettings: EventNetworkingSettings | null
  ) => void;
  // Event page specific accent color (bridges event settings to header)
  /** @deprecated Prefer useBrandingStore pageAccentColor */
  eventAccentColor: string | null;
  setEventAccentColor: (color: string | null) => void;
  // Event page specific logo (bridges event branding to header)
  /** @deprecated Prefer useBrandingStore overrides */
  eventLogo: string | null;
  setEventLogo: (logo: string | null) => void;
  // Event page specific favicon
  /** @deprecated Prefer useBrandingStore overrides */
  eventFavIcon: string | null;
  setEventFavIcon: (favIcon: string | null) => void;
  // Header visibility control for specific pages
  showHeader: boolean;
  setShowHeader: (show: boolean) => void;
};

const WhiteLabelContext = createContext<WhiteLabelContextType | null>({
  whiteLabel: null,
  branding: null,
  currentEventNetworkingSettings: null,
  setCurrentEventNetworkingSettings: () => {},
  eventAccentColor: null,
  setEventAccentColor: () => {},
  eventLogo: null,
  setEventLogo: () => {},
  eventFavIcon: null,
  setEventFavIcon: () => {},
  showHeader: true,
  setShowHeader: () => {},
});

interface WhiteLabelProviderProps {
  children: React.ReactNode;
  whiteLabel: (WhiteLabel & { integrations?: Integration[] }) | null;
  branding: ResolvedBranding | null;
}

export const WhiteLabelProvider = ({
  children,
  whiteLabel,
  branding,
}: WhiteLabelProviderProps) => {
  // Default visible so SSR/first paint includes the WL navbar. EventProviders
  // refines this from already-fetched eventSettings in useLayoutEffect
  // (showEventHeader / showCheckoutHeader / showThankYouHeader).
  const [currentEventNetworkingSettings, setCurrentEventNetworkingSettings] =
    useState<EventNetworkingSettings | null>(null);
  const [eventAccentColor, setEventAccentColor] = useState<string | null>(null);
  const [eventLogo, setEventLogo] = useState<string | null>(null);
  const [eventFavIcon, setEventFavIcon] = useState<string | null>(null);
  const [showHeader, setShowHeader] = useState(true);

  const value = {
    whiteLabel,
    branding,
    currentEventNetworkingSettings,
    setCurrentEventNetworkingSettings,
    eventAccentColor,
    setEventAccentColor,
    eventLogo,
    setEventLogo,
    eventFavIcon,
    setEventFavIcon,
    showHeader,
    setShowHeader,
  };

  return (
    <WhiteLabelContext.Provider value={value}>
      {children}
    </WhiteLabelContext.Provider>
  );
};

export const useWhiteLabel = () => {
  const context = useContext(WhiteLabelContext);
  if (!context) {
    throw new Error("useWhiteLabel must be used within a WhiteLabelProvider");
  }
  return context;
};
